import type { ApolloError } from '@apollo/client';
import { useEffect, useState } from 'react';

import { DesignConfigCategories } from 'src/modules/designStudio/constants/designConfig';
import type { MenuItemType } from 'src/modules/designStudio/constants/hardCodedData/textureData';
import { usePartner } from 'src/modules/designStudio/hooks';
import type {
  Geometry,
  Metadata,
} from 'src/modules/designStudio/hooks/useFetchModelData';
import {
  fetchMenuItems,
  fetchModelData,
  fetchPartner,
  fetchProductSettingOrgId,
  fetchProductSettings,
  fetchTextures,
  setMenuItems,
  updateProductSettingsOrgId,
} from 'src/modules/designStudio/machines/productCatalog/helpers';
import type {
  FilterTracker,
  FilteredColorsData,
  FilteredTexturesData,
  MenuItems,
  Textures,
} from 'src/modules/designStudio/machines/productCatalog/types';
import { Mode } from 'src/modules/designStudio/providers/ModeProvider';
import type { MenuItemsType } from 'src/modules/designStudio/providers/ProductCatalogProvider/index';
import type { GenericProductSettings } from 'src/shared/graphql/requests';
import { logError } from 'src/shared/telemetry/logger';

export type LegacyPCData = {
  textures: Textures;
  menuItems: MenuItemsType;
  filteredColors: FilteredColorsData;
  filteredTextures: FilteredTexturesData;
  filterTracker: FilterTracker;
  geometry: Geometry;
  metadata: Metadata;
  authToken?: string;
  shareError?: ApolloError;
};

/**
 * Execution Flow
 *
 * if(GUEST || PREVIEW || SHARE) {
 *
 *  fetchModelData();
 *
 *  fetchTextures();
 *  if(mode === DEFAULT) {
 *   fetchPartner();
 *  }
 *  setMenuItems();
 *
 * } else if(IR) {
 *
 *  fetchTextures();
 *  fetchPartner();
 *  setMenuItems();
 *
 * } else {
 *
 *  fetchProductSettingOrgId();
 *  updateProductSettingsOrgId();
 *  fetchProductSettings();
 *  updateProductSettings();
 *
 *  fetchModelData();
 *
 *  fetchTextures();
 *  fetchPartner();
 *  setMenuItems();
 *
 * }
 *
 */
export const useFetchPCData = (
  mode: Mode,
  jobId?: number,
  shareToken?: string,
  modelId?: number,
): LegacyPCData => {
  const defaultPartner = usePartner();
  const [menuItems, setMenuItemsState] = useState<MenuItemType[]>([]);
  const [textures, setTextures] = useState<Textures>(null);
  const [filteredColors, setFilteredColors] = useState<FilteredColorsData>({
    [DesignConfigCategories.ROOF]: [],
    [DesignConfigCategories.WALL]: [],
    [DesignConfigCategories.TRIM]: [],
    [DesignConfigCategories.WINDOW]: [],
  });
  const [filteredTextures, setFilteredTextures] =
    useState<FilteredTexturesData>({});
  const [filterTracker, setFilterTracker] = useState<FilterTracker>({});
  const [geometry, setGeometry] = useState<Geometry>(null);
  const [metadata, setMetadata] = useState<Metadata>(null);
  const [authToken, setAuthToken] = useState<string | undefined>(undefined);
  const [shareError, setShareError] = useState<ApolloError | undefined>(
    undefined,
  );

  const handleFetchingModelData = async () => {
    const data = await fetchModelData({
      mode,
      jobId,
      shareToken,
      modelId,
    });
    setMetadata(data.metadata);
    setGeometry(data.geometry);
    setAuthToken(data.authToken as string);
    setShareError(data.error);
  };

  const handleFetchingProductSettingsData =
    async (): Promise<GenericProductSettings | null> => {
      try {
        const data = await fetchProductSettingOrgId({ jobId });
        const { productSettingsOrgId } = updateProductSettingsOrgId(data as {});
        return await fetchProductSettings({ productSettingsOrgId });
      } catch (e) {
        logError(e as Error);
        return {};
      }
    };

  const handleFetchingTexturesData = async (
    settings: GenericProductSettings | null,
  ) => {
    const { textures: fetchedTextures } = await fetchTextures();
    let partner = defaultPartner;
    if (mode === Mode.DEFAULT) {
      const { partner: fetchedPartner } = await fetchPartner(
        jobId,
        defaultPartner,
      );
      partner = fetchedPartner;
    }

    const items = await fetchMenuItems(partner);
    const results = setMenuItems({
      menuItems: items as MenuItems,
      textures: fetchedTextures,
      productSettings: settings ?? undefined,
      partner,
      initialFilterTracker: {},
    });

    setTextures(fetchedTextures);
    setMenuItemsState(results.menuItems);
    setFilteredColors(results.filteredColors);
    setFilteredTextures(results.filteredTextures);
    setFilterTracker(results.filterTracker);
  };

  const [hasDataBeenFetched, setHasDataBeenFetched] = useState(false);

  useEffect(() => {
    if (!hasDataBeenFetched) {
      setHasDataBeenFetched(true);
      void (async () => {
        try {
          if (
            mode === Mode.GUEST ||
            mode === Mode.PREVIEW ||
            mode === Mode.SHARE
          ) {
            await handleFetchingModelData();
            await handleFetchingTexturesData(null);
          } else if (mode === Mode.INTERACTIVE_RENDERING) {
            await handleFetchingTexturesData(null);
          } else {
            const settings = await handleFetchingProductSettingsData();
            await handleFetchingModelData();
            await handleFetchingTexturesData(settings);
          }
        } catch (e) {
          logError(e as Error);
          throw e;
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    menuItems,
    textures,
    filteredColors,
    filteredTextures,
    filterTracker,
    geometry,
    metadata,
    authToken,
    shareError,
  };
};
